import { client } from "../../../api/client";

/**
 * Helpers
 */
const isLocal = () => process.env.REACT_APP_USELOCALDATA === "true";
const baseUri = process.env.REACT_APP_GATEWAY_URI;
const apiPath = "iquoteapi/quoterevision"; // maps to Route("api/[controller]") => "api/quoterevision"

const buildUrl = (endpoint) => `${baseUri}${endpoint}`;

// Best-effort on tab close/navigation. Uses keepalive and a cached token (set during normal API calls).
export const saveRevisionAdditionalInformationKeepalive = (quoteRevisionId, value) => {
    if (quoteRevisionId === null) return false;
    if (isLocal()) return true;

    try {
        const token = sessionStorage.getItem('iquote_idToken');
        if (!token) return false;

        const url = buildUrl(`${apiPath}/RevisionAdditionalInformation/${quoteRevisionId}`);
        const body = JSON.stringify(value ?? "");

        // Fire-and-forget; caller should not await.
        window.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'SourceApp': 'iQuoteNG'
            },
            body,
            keepalive: true
        });

        return true;
    } catch (error) {
        console.error("Error keepalive-saving revision additional information:", error);
        return false;
    }
};

export const saveRevisionAdditionalInformation = async (quoteRevisionId, value) => {
    if (quoteRevisionId === null) return [];

    if (isLocal()) {
        return [];
    } else {
        try {
            const response = await client.post(baseUri, `${apiPath}/RevisionAdditionalInformation/${quoteRevisionId}`, value);
            console.log(`Saved revision additional information ${quoteRevisionId}:`, response.data);
            return response.data;
        } catch (error) {
            if (error?.response?.status === 404) return []; // NotFound => no revisions
            console.error("Error saving revision additional information:", error);
            return [];
        }
    }
}

// Same endpoint, but returns the full client response so callers can check `ok`.
export const saveRevisionAdditionalInformationResult = async (quoteRevisionId, value) => {
    if (quoteRevisionId === null) return { ok: false, status: 400, data: null };
    if (isLocal()) return { ok: true, status: 200, data: null };

    try {
        return await client.post(baseUri, `${apiPath}/RevisionAdditionalInformation/${quoteRevisionId}`, value);
    } catch (error) {
        console.error("Error saving revision additional information:", error);
        return { ok: false, status: 500, data: null };
    }
};

export const fetchRevisionAdditionalInformation = async (quoteRevisionId) => {
    if (quoteRevisionId === null) return [];

    if (isLocal()) {
        return [];
    } else {
        try {
            const response = await client.get(baseUri, `${apiPath}/RevisionAdditionalInformation/${quoteRevisionId}`);
            console.log(`Fetched revision additional information ${quoteRevisionId}:`, response.data);
            return response.data;
        } catch (error) {
            if (error?.response?.status === 404) return []; // NotFound => no revisions
            console.error("Error fetching revision additional information:", error);
            return [];
        }
    }
}

/**
 * Fetch all quote revisions (optionally paginated client-side, optional client-side filter by quoteId).
 * Local mode expects ./_local/_quoteRevisions.json with { quoteRevisions: [...] }.
 * Returns: { quoteRevisions: QuoteRevision[], totalRecords: number }
 */
export const fetchQuoteRevisions = async (options = {}) => {
    const {
        pageNumber = 1,
        pageSize = 25,
        quoteId, // optional client-side filter while API has no filter params
    } = options;

    if (isLocal()) {
        try {
            let all = require("./_local/_quoteRevisions.json").quoteRevisions || [];
            if (quoteId != null) {
                all = all.filter(r => r.quoteId === quoteId);
            }
            const startIndex = (pageNumber - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            return { quoteRevisions: all.slice(startIndex, endIndex), totalRecords: all.length };
        } catch (error) {
            console.error("Error fetching quote revisions (local):", error);
            return { quoteRevisions: [], totalRecords: 0 };
        }
    } else {
        try {
            // Controller returns full list (DeleteDate == null); paginate/filter client-side for now
            const response = await client.get(baseUri, `${apiPath}`);
            let all = Array.isArray(response.data) ? response.data : [];
            if (quoteId != null) {
                all = all.filter(r => r.quoteId === quoteId);
            }
            const startIndex = (pageNumber - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            return { quoteRevisions: all.slice(startIndex, endIndex), totalRecords: all.length };
        } catch (error) {
            console.error("Error fetching quote revisions:", error);
            return { quoteRevisions: [], totalRecords: 0 };
        }
    }
};

/**
 * Fetch a single quote revision by id
 * Returns: QuoteRevision | null
 */
export const fetchQuoteRevision = async (id) => {
    if (!id && id !== 0) return null;

    if (isLocal()) {
        try {
            const all = require("./_local/_quoteRevisions.json").quoteRevisions || [];
            return all.find(r => r.id === id) ?? null;
        } catch (error) {
            console.error("Error fetching quote revision (local):", error);
            return null;
        }
    } else {
        try {
            const response = await client.get(baseUri, `${apiPath}/${id}`);

            if (!response.data) {
                return null
            }

            var quoteRevision = {
                ...response.data,

                quoteAdditionalFields: JSON.parse(response.data.quoteAdditionalFieldsJson),
                quoteOrderInformation: JSON.parse(response.data.quoteOrderInformationJson),
                quoteCustomisation: JSON.parse(response.data.quoteCustomisationJson)
            }

            console.log("quoteRevision", quoteRevision)

            return quoteRevision
        } catch (error) {
            if (error?.response?.status === 404) return null;
            console.error("Error fetching quote revision:", error);
            return null;
        }
    }
};

/**
 * Create a quote revision
 * Controller: POST api/quoterevision (accepts QuoteRevisionDTO)
 * Returns: server response (201) or a mock with newId in local
 */
export const createQuoteRevision = async (quoteRevision) => {
    if (isLocal()) {
        return { data: { newId: Math.floor(Math.random() * 100000) } };
    } else {
        try {
            var body = structuredClone(quoteRevision);
            /*
                Convert fields that need to be provided in JSON format
            */
            body.quoteAdditionalFieldsJson = JSON.stringify(body.quoteAdditionalFields, null, 2)
            body.quoteOrderInformationJson = JSON.stringify(body.quoteOrderInformation, null, 2)
            body.quoteCustomisationJson = JSON.stringify(body.quoteCustomisation, null, 2)

            return await client.post(baseUri, `${apiPath}`, body);
        } catch (error) {
            console.error("Error creating quote revision:", error);
            return [];
        }
    }
};

/**
 * Update a quote revision
 * Controller: PUT api/quoterevision/{id} (accepts QuoteRevisionDTO)
 */
export const updateQuoteRevision = async (quoteRevision) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            var body = structuredClone(quoteRevision);

            /*
                Convert fields that need to be provided in JSON format
            */
            body.quoteAdditionalFieldsJson = JSON.stringify(body.quoteAdditionalFields, null, 2)
            body.quoteOrderInformationJson = JSON.stringify(body.quoteOrderInformation, null, 2)
            body.quoteCustomisationJson = JSON.stringify(body.quoteCustomisation, null, 2)

            return await client.put(baseUri, `${apiPath}/${quoteRevision.id}`, body);
        } catch (error) {
            console.error("Error updating quote revision:", error);
            return [];
        }
    }
};

/**
 * Delete (soft delete) a quote revision
 * Controller: DELETE api/quoterevision/{id}
 */
export const deleteQuoteRevision = async (id) => {
    if (isLocal()) {
        return { data: { success: true } };
    } else {
        try {
            return await client.delete(baseUri, `${apiPath}/${id}`);
        } catch (error) {
            if (error?.response?.status === 404) {
                return { data: { notFound: true } };
            }
            console.error("Error deleting quote revision:", error);
            return [];
        }
    }
};

/**
 * Convenience: get raw array without paging/slicing
 */
export const fetchAllQuoteRevisionsRaw = async () => {
    if (isLocal()) {
        try {
            return require("./_local/_quoteRevisions.json").quoteRevisions || [];
        } catch (error) {
            console.error("Error fetching quote revisions raw (local):", error);
            return [];
        }
    } else {
        try {
            const response = await client.get(baseUri, `${apiPath}`);
            return response.data ?? [];
        } catch (error) {
            console.error("Error fetching quote revisions raw:", error);
            return [];
        }
    }
};

/**
 * Optional: fetch all revisions for a specific quoteId
 * Returns: array of QuoteRevision for the given quoteId
 */
export const fetchQuoteRevisionsByQuoteId = async (quoteId, quoteRevisionsFilter) => {
    if (quoteId == null) return [];

    if (isLocal()) {
        try {
            const all = require("./_local/_quoteRevisions.json").quoteRevisions || [];
            return all.filter(r => r.quoteId === quoteId);
        } catch (error) {
            console.error("Error fetching quote revisions by quoteId (local):", error);
            return [];
        }
    } else {
        try {
            const response = await client.post(baseUri, `${apiPath}/ByQuoteId/${quoteId}`, quoteRevisionsFilter);
            console.log(`Fetched revisions for quoteId ${quoteId}:`, response.data);
            const all = Array.isArray(response.data.data) ? response.data.data : [];

            return {
                revisions: all.map(revision => ({
                    ...revision,
                    quoteAdditionalFields: JSON.parse(revision.quoteAdditionalFieldsJson),
                    quoteOrderInformation: JSON.parse(revision.quoteOrderInformationJson),
                    quoteCustomisation: JSON.parse(revision.quoteCustomisationJson)
                })), totalRecords: response.data.totalRecords
            };
        } catch (error) {
            if (error?.response?.status === 404) return []; // NotFound => no revisions
            console.error("Error fetching quote revisions by quoteId:", error);
            return [];
        }
    }
};

export const fetchQuoteRevisionsDropdown = async (quoteId) => {
    if (quoteId === null) return [];

    if (isLocal()) {
        try {
            const all = require("./_local/_quoteRevisions.json").quoteRevisions || [];
            return all.filter(r => r.quoteId === quoteId);
        } catch (error) {
            console.error("Error fetching quote revisions dropdown by quoteId (local):", error);
            return [];
        }
    } else {
        try {
            const response = await client.get(baseUri, `${apiPath}/Dropdown/${quoteId}`);
            console.log(`Fetched revisions dropdown for quoteId ${quoteId}:`, response.data);

            return response.data;
        } catch (error) {
            if (error?.response?.status === 404) return []; // NotFound => no revisions
            console.error("Error fetching quote revisions dropdown by quoteId:", error);
            return [];
        }
    }
}